import SEO from "@/components/SEO";
import { useReveals, Counter } from "@/components/Reveal";

const STATS = [
  { value: 25, suffix: "+", label: "Years of Excellence" },
  { value: 100, suffix: "+", label: "Countries" },
  { value: 500, suffix: "+", label: "Partners" },
  { value: 10, suffix: "K+", label: "Shipments" },
];

const EVOLUTION = [
  {
    mark: "1999",
    title: "Founded",
    body: "OA Holding CO LTD. is founded with a vision to bring innovation, reliability and exceptional service to the shipping industry.",
  },
  {
    mark: "—",
    title: "Global Network",
    body: "What started as a small maritime operation grows into a global enterprise spanning over 100 countries, with a vast network of partners and agents.",
  },
  {
    mark: "—",
    title: "Logistics",
    body: "A one-stop-shop for shipping: ocean freight, land transportation, warehousing, global logistics, port and container services.",
  },
  {
    mark: "—",
    title: "Technology",
    body: "The same standard of excellence extended into software — custom development, process automation and integration, digital transformation and consulting.",
  },
  {
    mark: "2024",
    title: "Digital Infrastructure",
    body: "EuroVDC Datacenter established in Bulgaria — a state-of-the-art, fully operational data center built on years of data center operations expertise.",
  },
  {
    mark: "Today",
    title: "One Holding",
    body: "A diversified holding across shipping, software, data center infrastructure, maritime assets, e-commerce, AI and hospitality.",
  },
];

const VALUES = [
  {
    id: "01",
    title: "Our Mission",
    body: "To provide reliable and efficient shipping services to businesses and individuals worldwide — striving to exceed expectations through timely deliveries, competitive prices and exceptional customer service.",
    img: "https://images.unsplash.com/photo-1645736315000-6f788915923b?w=800&q=80",
    alt: "Cargo operations representing OA Holding's mission",
  },
  {
    id: "02",
    title: "Sustainability",
    body: "Committed to reducing our carbon footprint and minimizing the environmental impact of our operations — using eco-friendly packaging materials and working with partners on sustainable shipping solutions.",
    img: "https://images.unsplash.com/photo-1611224111800-0eaf3e53aa45?w=800&q=80",
    alt: "Sustainable shipping practices",
  },
  {
    id: "03",
    title: "Global Network",
    body: "A presence in over 100 countries, with a vast network of partners and agents enabling seamless shipping services across the globe.",
    img: "https://images.unsplash.com/photo-1613690399151-65ea69478674?w=800&q=80",
    alt: "OA Holding's global network",
  },
];

export default function About() {
  useReveals();
  return (
    <main data-testid="about-page">
      <SEO
        title="About OA Holding CO LTD. — Building Excellence Since 1999"
        description="OA Holding CO LTD. — founded in 1999, grown from a maritime operation into a global enterprise spanning 100+ countries across shipping, software and digital infrastructure."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About OA Holding CO LTD.",
          url: "https://oaholding.co/about",
        }}
      />

      {/* editorial opening */}
      <section className="px-[6vw] pb-16 pt-[22vh] md:pt-[26vh]">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs" data-reveal>
          About OA
        </p>
        <h1 className="mt-6 font-display text-[14vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[9vw]" data-reveal>
          OA Holding
        </h1>
        <p className="mt-10 max-w-3xl font-display text-2xl font-bold leading-snug tracking-tight md:text-4xl" data-reveal>
          What started as a small maritime operation has grown into a global
          enterprise spanning over{" "}
          <span className="text-amber">100 countries</span> — moving goods, data
          and business since 1999.
        </p>
        <p className="mt-8 max-w-2xl text-sm leading-relaxed text-smoke md:text-base" data-reveal>
          Today, OA Holding stands as a testament to what is possible when
          passion meets purpose — a journey defined by commitment to customers,
          dedication to sustainability, and a drive to embrace technological
          advancement. Beyond shipping, we have expanded into software
          solutions, bringing the same level of excellence and innovation to
          help businesses thrive in the digital age.
        </p>
      </section>

      {/* story image */}
      <section className="px-[6vw] pb-24">
        <div className="overflow-hidden" data-mask>
          <img
            src="https://images.unsplash.com/photo-1589779255235-85dc2a054145?w=1600&q=80"
            alt="OA Holding — global enterprise"
            className="h-[45vh] w-full object-cover brightness-[0.8] contrast-[1.05] md:h-[70vh]"
            loading="lazy"
            decoding="async"
            data-parallax
          />
        </div>
      </section>

      {/* verified metrics */}
      <section className="border-y border-white/10" data-testid="about-stats">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`px-[6vw] py-10 md:px-10 md:py-16 ${i > 0 ? "border-l border-white/10" : ""} ${i > 1 ? "max-md:border-t max-md:border-white/10" : ""} ${i === 2 ? "max-md:border-l-0" : ""}`}
              data-testid={`about-stat-${i}`}
            >
              <div className="font-display text-5xl font-black tracking-tight text-bone md:text-7xl">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-amber">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* evolution */}
      <section className="px-[6vw] py-24 md:py-32" data-testid="about-evolution">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber" data-reveal>
          Evolution
        </p>
        <h2 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter md:text-6xl" data-reveal>
          Building excellence
          <br />
          since 1999.
        </h2>
        <div className="relative mt-16 border-l border-white/15 pl-8 md:pl-14">
          {EVOLUTION.map((e, i) => (
            <div key={e.title} className={`relative ${i > 0 ? "mt-14" : ""}`} data-reveal data-testid={`evolution-${i}`}>
              <span className="absolute -left-8 top-2 h-px w-6 bg-amber md:-left-14 md:w-10" />
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">{e.mark}</p>
              <h3 className="mt-2 font-display text-2xl font-black uppercase tracking-tight md:text-4xl">
                {e.title}
              </h3>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-smoke md:text-base">{e.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* values */}
      <section className="border-t border-white/10 px-[6vw] py-24 md:py-32">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber" data-reveal>
          Our Values
        </p>
        <h2 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter md:text-6xl" data-reveal>
          What drives us forward.
        </h2>
        <div className="mt-16 space-y-20">
          {VALUES.map((v, i) => (
            <div
              key={v.id}
              className={`grid items-center gap-10 md:grid-cols-2 md:gap-16 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
              data-testid={`value-${v.id}`}
            >
              <div className="overflow-hidden" data-mask>
                <img
                  src={v.img}
                  alt={v.alt}
                  className="h-[36vh] w-full object-cover brightness-[0.8] md:h-[52vh]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div data-reveal>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">{v.id}</p>
                <h3 className="mt-4 font-display text-3xl font-black uppercase tracking-tight md:text-5xl">
                  {v.title}
                </h3>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-smoke md:text-base">{v.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
